import { Button, Typography } from '@mui/material';
import { useCommunityPointsPerActions } from 'admin-features/settings/hooks';
import { Formik } from 'formik';
import { useCommunityContext } from 'providers';
import { ReactNode, useMemo } from 'react';

import { RewardTransactionAction } from '@jebel/constants';

import { LoadingOverlay, Skeleton, Stack } from 'shared/components/ui';
import { useSetPointsPerActionMutation } from 'shared/graphql';
import { useToast } from 'shared/hooks';

import { SettingsCommunityPointsPerAction } from './SettingsCommunityPointsPerAction';
import {
  Container,
  Content,
  Footer,
  Header,
  ActionsOptions,
  Section,
  SectionTitle,
} from './SettingsCommunityRewarding.styles';

interface FormValues {
  [RewardTransactionAction.AdViewed]: number;
  [RewardTransactionAction.PostCreated]: number;
  [RewardTransactionAction.PostLiked]: number;
  [RewardTransactionAction.CommentCreated]: number;
  [RewardTransactionAction.DiscountRedeemed]: number;
  [RewardTransactionAction.BusinessClaimed]: number;
  [RewardTransactionAction.EventAttended]: number;
}

interface ThresholdPreset {
  title: string;
  description: ReactNode;
  subtitle?: string;
  field: RewardTransactionAction;
  disabled?: boolean;
}

const ACTIONS_OPTIONS: ThresholdPreset[] = [
  {
    title: 'Ad Impression',
    description: 'Points given for Ad Impressions by user',
    field: RewardTransactionAction.AdViewed,
  },
  {
    title: 'Published Post',
    field: RewardTransactionAction.PostCreated,
    description: 'Points given for publishing a post.',
  },
  {
    title: 'Post Reaction',
    description: 'Points given for liking a post',
    field: RewardTransactionAction.PostLiked,
  },
  {
    title: 'Post Comment (Coming Soon)',
    description: 'Points given for commenting on a post.',
    field: RewardTransactionAction.CommentCreated,
    disabled: true,
  },
  {
    title: 'Discount Redeemed',
    description: 'Points given by redeeming a discount.',
    field: RewardTransactionAction.DiscountRedeemed,
  },
  {
    title: 'Business Claimed',
    description: 'Points given for claiming a business.',
    field: RewardTransactionAction.BusinessClaimed,
  },
  {
    title: 'Event Attended',
    description: 'Points given for attending an event',
    field: RewardTransactionAction.EventAttended,
  },
];

const SAVING_MESSAGE_KEY = 'SAVING_SETTINGS_COMMUNITY_REWARDING';

/**
 * Setup the community points per actions settings for the community.
 * @ticket https://github.com/jebelapp/jebel/issues/1834
 */
export function SettingsCommunityRewarding() {
  const { selectedCommunity: community, loading: isCommunityLoading } = useCommunityContext();
  const { showMessage, showSuccess, showError, dismiss } = useToast();

  const { data: pointsPerActions, loading: isPointsPerActionsLoading } =
    useCommunityPointsPerActions();

  const [setPointsPerAction] = useSetPointsPerActionMutation({
    refetchQueries: ['GetPointsPerActions'],
    awaitRefetchQueries: true,
  });

  const isLoading = isCommunityLoading || isPointsPerActionsLoading;

  const initials = useMemo<FormValues>(() => {
    const initials: FormValues = {
      ...pointsPerActions,
    };

    return initials;
  }, [pointsPerActions]);

  const handleSubmit = async (values: FormValues) => {
    const actions = Object.keys(values) as RewardTransactionAction[];

    try {
      showMessage(`Saving rewarding preferences, please stand by...`, {
        id: SAVING_MESSAGE_KEY,
      });

      await Promise.all(
        actions.map(async action => {
          const curr = values[action] ?? 0;
          const prev = initials[action] ?? 0;

          if (curr === prev) {
            return;
          }

          if (!community) {
            return;
          }

          await setPointsPerAction({
            variables: {
              community: { id: community.id! },
              action: action,
              points: curr,
            },
          });
        }),
      );

      showSuccess('Successfully updated rewarding preferences');
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(SAVING_MESSAGE_KEY);
    }
  };

  return (
    <Container>
      <Header>
        <Typography variant="h6">Rewards Points</Typography>
      </Header>

      <Formik initialValues={initials} enableReinitialize onSubmit={handleSubmit}>
        {form => (
          <Content>
            <Section>
              <SectionTitle>Assigned Point Values</SectionTitle>

              <Typography>Enter the point values for each user action below.</Typography>

              <LoadingOverlay
                loading={isLoading}
                overlay={
                  <ActionsOptions>
                    {Array.from({ length: ACTIONS_OPTIONS.length }).map((_, index) => (
                      <Skeleton key={index} variant="rect" height={120} />
                    ))}
                  </ActionsOptions>
                }
              >
                <ActionsOptions>
                  {ACTIONS_OPTIONS.map(preset => (
                    <SettingsCommunityPointsPerAction
                      key={preset.title}
                      title={preset.title}
                      description={preset.description}
                      field={preset.field}
                      disabled={preset.disabled}
                    />
                  ))}
                </ActionsOptions>
              </LoadingOverlay>

              <Typography variant="caption" color="textSecondary">
                * Changes made here will only apply to future rewards. Existing rewards will remain
                unaffected.
              </Typography>
            </Section>

            <Footer>
              <Stack direction="row" spacing={1}>
                <Button
                  disabled={!form.dirty || form.isSubmitting}
                  variant="contained"
                  color="primary"
                  onClick={form.submitForm}
                >
                  Save
                </Button>

                <Button
                  disabled={!form.dirty || form.isSubmitting}
                  variant="outlined"
                  onClick={() => form.resetForm()}
                >
                  Cancel
                </Button>
              </Stack>
            </Footer>
          </Content>
        )}
      </Formik>
    </Container>
  );
}
